import { Injectable } from '@angular/core';
import { ProductService } from '../product.service';
import { FormComponent } from '../../models/FormComponent';

export enum ValidationType {
  Error,
  Alert
}

// export enum AlertType {
//   Sku, 
//   Price
// }


export enum ValidationActionType{
  AddProduct,
  EditProduct
}

export interface ValidationErrorSettings {
  fieldNameForMessage:string, 
  required:boolean, 
  minlength:number,
  numericOnly?:boolean
}

export interface ValidateAlertFunction  { (formComponent:FormComponent, vas:ValidationAlertSettings): Validation }

export interface ValidationAlertSettings {
  fieldNameForMessage:string, 
  required:boolean, 
  validateAlertFunction:any
}

export interface Validation {
  validationType:ValidationType
  formComponent:FormComponent, 
  success:boolean, 
  message:string
}

@Injectable()
export class ProductValidatorService {
  public alertValidations:Validation[] = []
  public errorValidations:Validation[] = [];
  formComponents: FormComponent[] = [];  //to trigger all formcomponents
  

  constructor(
    private productService:ProductService,
  ) { }

  fieldValidator(fieldName:string, fieldValue:string, fieldNameForMessage?:string, required?:boolean, minlength?:number, numericOnly?:boolean): {success:boolean, message:string} {
    //validations
    let message = 'no message';
    let success = false;
    
    //1 - invalid 'required'
    if(required && (fieldValue==undefined || fieldValue==='')){ message = fieldNameForMessage + ' is required' }
    //2 - invalid 'minlength'
    else if(minlength && fieldValue.length < minlength){ message = fieldNameForMessage+' must be at least '+ minlength +' characters' } 
    //3 - invalid numericOnly
    else if(numericOnly && isNaN(Number(fieldValue))){ message = fieldNameForMessage+' must be a numeric value' }
    //4 - valid
    else { success = true }

    //store and return result
    let result = {success: success, message:message};
    // this.validations[fieldName] = result;
    return result;
  }

  validateForError(formComponent:FormComponent, ves:ValidationErrorSettings)
  : Validation {
    //unwrap formComponent and ves
    let fieldName=formComponent.fieldId, fieldValue=formComponent.fieldValue;
    let fieldNameForMessage=ves.fieldNameForMessage, required=ves.required, minlength=ves.minlength, numericOnly=ves.numericOnly;
    //default result object
    let result:Validation = {validationType:ValidationType.Error, formComponent:formComponent, success:false, message:'no message'};
    //1 - blank field
    let fieldIsBlank = (fieldValue==undefined || fieldValue==='');
    if(fieldIsBlank){ 
      if(required){
        // 1a. 'required' but blank
        result.success = false;
        result.message = fieldNameForMessage + ' is required' 
      } else {
        //1b. 'not required'
        result.success = true;
        result.message = 'Not requried'
      }
    }
    //2 - invalid 'minlength'
    else if(minlength && fieldValue.length < minlength){ 
      result.success = false;
      result.message = fieldNameForMessage+' must be at least '+ minlength +' characters';
    } 
    //3 - invalid numericOnly
    else if(numericOnly && isNaN(Number(fieldValue))){ 
      result.success = false;
      result.message = fieldNameForMessage+' must be a numeric value';
    }
    //4 - valid
    else { 
      result.success = true; 
      result.message = 'No errors';
    }

    //store and return result
    this.errorValidations.push(result);
    return result;
  }

  public validateForAlertFunction(formComponent:FormComponent, vas:ValidationAlertSettings)
  :Validation {
    //unwrap formComponent and vas
    // let fieldName=formComponent.fieldId, fieldValue=formComponent.fieldValue;
    // let fieldNameForMessage=vas.fieldNameForMessage, required=vas.required;
    // let result:Validation = {validationType:ValidationType.Alert, formComponent:formComponent, success:false, message:'no message'};
    // let fieldIsBlank = (fieldValue==undefined || fieldValue==='');
    let validateAlertFunction = vas.validateAlertFunction;

    let result = validateAlertFunction(formComponent, vas);
    
    return result;
  }


  // Defined as an INSTANCE FUNCTION.  Note the syntax with AF.  
  // This is to preserve 'this' context, so that function can be called via a function reference in validateForAlertFunction();
  // https://github.com/Microsoft/TypeScript/wiki/%27this%27-in-TypeScript
  public validateForSkuAlert = (formComponent:FormComponent, vas:ValidationAlertSettings) => {
    //unwrap formComponent and vas
    let fieldName=formComponent.fieldId, fieldValue=formComponent.fieldValue;
    let fieldNameForMessage=vas.fieldNameForMessage, required=vas.required;
    //default result
    let result:Validation = {validationType:ValidationType.Alert, formComponent:formComponent, success:false, message:'no message'};
    //guard validationError didn't occur (can only show EITHER error OR alert)
    let errorValidation = this.validationForElementId(fieldName, ValidationType.Error);
    // console.log('errorValidation', errorValidation);
    if(errorValidation && errorValidation.success == false){
      console.log('no alert for '+fieldName+' due to occurance of failed errorValidation', errorValidation);
      return result;
    }
    let productId = formComponent.product.product_id;
    let action = productId===undefined ? ValidationActionType.AddProduct : ValidationActionType.EditProduct;
    //1 - guard against validating sku when editing a product who's sku hasn't changed.
    if(action == ValidationActionType.EditProduct){
      let originalProduct = this.productService.getProductById(productId);
      let originalSku = originalProduct.sku;
      if(fieldValue == originalSku){
        result.success = true;
        result.message = 'Original value';
        this.alertValidations.push(result);
        return result;
      } 
    } 
    //continue with validating the sku
    //2 - sku is being added or edited with a new value, so validate that it isn't yet taken
    let skuExists = this.productService.checkSkuExists(fieldValue);
    if (skuExists) {
      result.success = false;
      result.message = 'Sku already taken';
    }
    else {
      result.success = true;
      result.message = 'Available';
    }

    this.alertValidations.push(result);
    return result;
    
  }


  //Defined as an INSTANCE Function
  public validateForPriceAlert = (formComponent:FormComponent, vas:ValidationAlertSettings) => {
    //unwrap formComponent and vas
    let product=formComponent.product, fieldName=formComponent.fieldId, fieldValue=formComponent.fieldValue;
    let fieldNameForMessage=vas.fieldNameForMessage, required=vas.required;
    //default result
    let result:Validation = {validationType:ValidationType.Alert, formComponent:formComponent, success:false, message:'no message'};
    //1 - guard against inactive product (pass validationAlert for inactive product)
    if(!product.active){
      result.success = true;
      result.message = '';
      return result;
    }
    //1 - guard against blank price
    let fieldIsBlank = (fieldValue==undefined || fieldValue==='');
    if(fieldIsBlank){
      result.success = false;
      result.message = 'Price cannot be blank';
      this.alertValidations.push(result);
      return result;
    }
    //2 - proceed with validation to show alert for price: 'too low' or 'too high'
    let priceValue = +fieldValue;
    if (priceValue <= 0) {
      result.success = false;
      result.message = 'Price is too low';

    }
    else if (priceValue >= 100) {
      result.success = false;
      result.message = 'Price is very high';
    }
    else {
      result.success = true;
      result.message = '';
    }

    this.alertValidations.push(result);
    return result;

  }


  public validationForElementId(elementId:string, validationType:ValidationType): Validation {
    let validations = [];
    switch (validationType) {
      case ValidationType.Error:
        validations=this.errorValidations;
        break;
      case ValidationType.Alert:
        validations=this.alertValidations;
      break;
      default:
      console.log('ValidationType'+validationType+'not handled.  Returning null');
        return null;
    }
    
    let matchingValidation = validations.filter((validation) => {
      return validation.formComponent.fieldId == elementId;
    })[0];
    return matchingValidation;
  }

  public registerFormComponent(formComponent:FormComponent){
    this.formComponents.push(formComponent);
  }


  public triggerValidationsForAllFormComponents(showErrorsAndAlerts:boolean) {
    for(let formComponent of this.formComponents){
      formComponent.triggerValidationsforParent(showErrorsAndAlerts);
    }
  }

  public validationErrors(){
    //reduce to errors
    let validationErrors = this.errorValidations.filter((validation) => {
      let isError = (validation.success == false);
      return isError;
    });
    return validationErrors;
  }

  public validationAlerts(){
    //reduce to alerts
    let validationAlerts = this.alertValidations.filter((validation) => {
      let redAlert = (validation.success == false);
      return redAlert;
    });
    return validationAlerts;
  }


  //filter components with this fieldName 
  public resetValidationForField(fieldName:string){
   let filteredErrorValidations = this.errorValidations.filter((errorValidation) => {
     return errorValidation.formComponent.fieldId != fieldName;
   });
   let filteredAlertValidations = this.alertValidations.filter((alertValidation) => {
    return alertValidation.formComponent.fieldId != fieldName;
    });
   
    this.errorValidations = filteredErrorValidations;
    this.alertValidations = filteredAlertValidations;
  }

  public reset(){
    this.formComponents = [];
    this.errorValidations = [];
    this.alertValidations = [];
  }



  // public removeErrorValidationForField(fieldName:string){
  //   delete this.errorValidations[fieldName];
  // }

  // public removeAlertValidationForField(fieldName:string){
  //   delete this.alertValidations[fieldName];
  // }


  // public setValidationResult(fieldName:string, result:{success:boolean, message:string}){
  //   this.validations[fieldName] = result;
  // }


}



//======================
//Unused Code



  // skuValidator(sku): {success: boolean, message:string} {
  //   //validations
  //   let message = 'no message';
  //   let success = false;
    
  //   //1 - empty
  //   if(sku == ''){ message = 'Sku is required' }
  //   //2 - minlength
  //   else if(sku.length < 4){ message = 'Sku must be at least 4 characters' } 
  //   //3 - valid
  //   else { success = true }

  //   //store and return result
  //   let result = {success: success, message:message};
  //   this.validations['sku'] = result;
  //   return result;
  // }


  // categoryValidator(category): {success:boolean, message:string} {
  //       //validations
  //       let message = 'no message';
  //       let success = false;
        
  //       //1 - empty
  //       if(category == ''){ message = 'Category is required' }
  //       //2 - minlength
  //       else if(category.length < 4){ message = 'Category must be at least 4 characters' } 
  //       //3 - valid
  //       else { success = true }
    
  //       //store and return result
  //       let result = {success: success, message:message};
  //       this.validations['category'] = result;
  //       return result;
  // }

  // imageSummaryValidator(imageSummary): {success:boolean, message:string} {
  //       //validations
  //       let message = 'no message';
  //       let success = false;
        
  //       //1 - empty
  //       if(imageSummary == ''){ message = 'Filename is required' }
  //       //2 - minlength
  //       else if(imageSummary.length < 5){ message = 'Filename must be at least 5 characters' } 
  //       //3 - valid
  //       else { success = true }
    
  //       //store and return result
  //       let result = {success: success, message:message};
  //       this.validations['imageSummary'] = result;
  //       return result;
  // }

  // imageDetailValidator(imageDetail): {success:boolean, message:string} {
  //   //validations
  //   let message = 'no message';
  //   let success = false;
    
  //   //1 - empty
  //   if(imageDetail == ''){ message = 'Filename is required' }
  //   //2 - minlength
  //   else if(imageDetail.length < 5){ message = 'Filename must be at least 5 characters' } 
  //   //3 - valid
  //   else { success = true }

  //   //store and return result
  //   let result = {success: success, message:message};
  //   this.validations['imageDetail'] = result;
  //   return result;
  // }

  // // titleValidator(title): {success:boolean, message:string} {
    
  // // }

// delete this.validations[fieldName];
// errorValidations: string[] = [];
  // alertValidations: string[] = [];



//   public validationAlerts(requiredOnly:boolean){
//     //reduce to alerts
//     let validationAlerts = this.validations.filter((validation) => {
//      let redAlert = validation.success == false && (validation.validationFlashType == ValidationFlashType.AlertSku || validation.validationFlashType == ValidationFlashType.AlertPrice);
//      let required = requiredOnly ? validation.formComponent.validationSettings.required : true;
//      return redAlert && required;
//    });
//    return validationAlerts;
//  }

  // public validationErrors(){
  //   //reduce to errors
  //   let validationErrors = this.validations.filter((validation) => {
  //     let isError = validation.success == false && validation.validationFlashType == ValidationFlashType.Error;
  //     return isError;
  //   });
  //   return validationErrors;    
  // }


  //function signature, so that ValidationAlertSettings could hold a reference to the alertCallback.
// //  *however, I used optional (protected) method in FormComponent superclass instead.
// export interface AlertCallbackType { (result:Validation): void }

// export interface ValidationAlertSettings {
//   fieldNameForMessage:string, 
//   required:boolean, 
//   alertCallback?:AlertCallbackType
// }

// switch (alertType) {
//   case AlertType.SkuAlert:
//     result = this.validateForSkuAlert(formComponent, fieldName, fieldValue);    
//     break;
//   case AlertType.PriceAlert:
//     result = this.validateForPriceAlert(formComponent, fieldName, fieldValue);
//     break;
//   default:
//     console.log('Warning: Product-validator encountered unhandled AlertType', alertType);
//     break;
// }



  // let errorValidation = this.validationForElementId(fieldName, ValidationType.Error);  //fieldName is being used for the elementId
  //Local Fat Arrow
  // https://github.com/Microsoft/TypeScript/wiki/%27this%27-in-TypeScript
  // someCallback((n, m) => x.doSomething(n, m));
  // console.log('validateForSkuAlert: this', this);
  // let internalValidation = (() => {
    // return this.validationForElementId(fieldName, ValidationType.Error) as Validation;
  // });
  // let errorValidation = internalValidation();


// WEBPACK FOOTER //
// ./src/app/services/validators/product-validator.service.ts